import { curry, slice, isNil, isEmpty } from 'ramda';

// Escape string for use in a RegEx
// https://stackoverflow.com/questions/3561493/is-there-a-regexp-escape-function-in-javascript/3561711#3561711
export const escapeStringForRegex = str => str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

export const bindMethods = (thisObj, ...methodNames) => {
    methodNames.forEach(
        (methodName) => {
            try {
                thisObj[methodName] = thisObj[methodName].bind(thisObj);
            } catch (error) {
                throw new Error(`Method "${methodName}" not found`);
            }
        },
    );
};

export const safeToLower = maybeString => typeof maybeString === 'string'
    ? maybeString.toLowerCase()
    : maybeString;

export const getByID = (id, collection) => collection.find(x => x.id === id);

export const stringContains = (string, searchTerm, caseSensitive = true) => typeof string === 'string' && typeof searchTerm === 'string'
    ? caseSensitive
        ? string.indexOf(searchTerm) !== -1
        : string.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1
    : false;

export const replaceAll = (string, substring, newSubstring) => string.replace(RegExp(escapeStringForRegex(substring), 'g'), newSubstring);

export const stringReplace = (string, ...replacementParameters) => replacementParameters.length > 0 ?
    replacementParameters.reduce(((reduceString, parameter) => {
        // / \${.*?} /
        // \$ matches the character $ literally (case sensitive)
        // { matches the character { literally (case sensitive)
        // .*? matches any character (except for line terminators)
        // *? Quantifier — Matches between zero and unlimited times, as few times as possible, expanding as needed (lazy)
        // } matches the character } literally (case sensitive)
        const valueReferenceRegEx = /\${.*?}/;

        // This function accepts different types of parameters
        // e.g. stringReplace('${test}','replaceFirstReference','replaceSecondReference'])
        if (typeof (parameter) === 'string') {
            return reduceString.replace(valueReferenceRegEx, parameter);
        }

        // e.g. stringReplace('${test}',['replaceFirstReference','replaceSecondReference'])
        if (Array.isArray(parameter)) {
            return parameter.reduce(
                (reduceStringList, value) => reduceStringList.replace(valueReferenceRegEx, value),
                reduceString,
            );
        }

        // e.g. stringReplace('${test}',{test,'replace'})
        if (parameter === Object(parameter)) {
            return Object.entries(parameter).reduce(
                (reduceStringObject, [key, value]) => replaceAll(reduceStringObject, `\${${key}}`, value),
                reduceString,
            );
        }

        // any invalid case
        return reduceString;
    }), string) :
    string;

export const takePage = curry(
    (pageSize, pageNumber, list) => {
        const start = (pageNumber - 1) * pageSize;
        const end = start + pageSize;
        return slice(start, end, list);
    },
);

export const isNullOrEmpty = item => isNil(item) || isEmpty(item);

// Value references appear as {![ValueName].[PropertyName]} OR {![ValueName]}
export const decodeValueReference = (referenceText) => {
    // Found by pattern {![ text ]
    const re1 = /{!\[(.*?)\].*/;
    const valueName = re1.test(referenceText) ?
        referenceText.replace(re1, '$1') :
        null;

    // Found by pattern .[ text ]
    const re2 = /.*\.\[(.*?)\].*/;
    const propertyName = re2.test(referenceText) ?
        referenceText.replace(re2, '$1') :
        null;

    return ({
        valueName,
        propertyName,
    });
};

/**
 * @description Indicates whether a tenant name belongs to a subtenant
 * @param {string} tenantName
 * @returns {boolean}
 */
export const isSubtenant = tenantName => stringContains(tenantName, '+');

export const formatJSONContent = (contentJSON) => {
    try {
        return JSON.stringify(JSON.parse(contentJSON), null, '\t');
    } catch (error) {
        return contentJSON;
    }
};

export const isNullOrWhitespace = text => isNullOrEmpty(text) || isNullOrEmpty(text.trim());

export const generateRandomString = (length) => {
    let text = '';
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < length; i += 1) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
};

/**
 * 
 * @param {String} tabType e.g flow, page, values etc
 * @param {String} flowId 
 * @param {String} pageId 
 * @param {String} tenantId 
 * @param {String} tabKey A guid to include as a url query parameter. This is used as an identifier for tabs
 * that can be opened multiple times but are not associated to a unique resource. For example, the players tab.
 * @param {String} pageName Applicable for when routing to the new page tab.
 * 
 * @description This function is for creating a tab url. The url must be in the following format:
 *      - /:tenant-id/:tab-name/:flow/page-id
 * 
 * A url is typically generated when switching between tabs and opening new tabs.
 */
export const generateRouteUrl = ({ tabType, tenantId, options: { flowId, pageId, tabKey, pageName = 'New Page' } = {} }) => {
    let urlId = null;
    let type = null;

    switch (tabType) {
    case 'flow':
        urlId = flowId;
        type = 'flows';
        break;

    case 'page':
        urlId = pageId;
        type = 'pages';
        break;
    default:
        urlId = null;
        type = tabType;
    }

    let url = `/${tenantId}`;

    url += `/${type}`;

    if (['api', 'players', 'macros'].includes(type) === true) {
        url += `?tab=${tabKey}`;
    }

    if (type === 'new_page') {

        // The name of a new page can be defined prior to page creation (from the flow canvas).
        // We need to include as a query parameter so that the name can be extracted from the url
        // once the page editor component has mounted.
        url += `?tab=${tabKey}&page_name=${pageName}`;
        if (pageId) {

            // Due to system flow dependancy, a page ID must be supplied when
            // creating a new page from the canvas. The ID is generated by the page map element system flow
            // and is used to reference the new page selected in the page element config panel.
            // Appending the ID to the url as we navigate is the only way for our custom routing hook
            // to pass this ID into the Alt store, where it is eventually picked up by the page editor.
            url += `&page_id=${pageId}`;
        }
    }

    if (urlId) {
        url += `/${urlId}`;
    }

    return url;
};

export const selectStyles = {
    control: provided => ({
        ...provided,
        boxShadow: 'none',
        borderColor: '#ccc',
        ':hover': {
            ...provided[':hover'],
            borderColor: '#ccc',
        },
    }),
    noOptionsMessage: provided => ({
        ...provided,
        textAlign: 'left',
    }),
};
